"use client";
import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { Typography } from "@/components/ui";
import { env } from "@/lib/environment";
import { AppList } from "@/lib/data/industryType";

export interface AppListSectionProps {
  apps: AppList[];
  viewAllHref?: string;
  viewAllText?: string;
  autoRotateInterval?: number;
  className?: string;
  imgBaseUrl?: string;
  centerTitle?: boolean; // Option to center the h2 title
  activeBorderColor?: string; // Option to customize active card border (e.g., "red", "primary")
  showViewAppLink?: boolean; // Option to show/hide "View This App" link
  showPreviewImage?: boolean; // Option to show/hide preview image
  useSimpleImage?: boolean; // Option to use simple image without absolute positioning (default: false, uses complex structure)
}

export default function AppListSection({
  apps,
  viewAllHref = "https://www.pickcel.com/digital-signage-apps",
  viewAllText = "View All",
  autoRotateInterval = 8000,
  className = "",
  imgBaseUrl,
  centerTitle = false,
  activeBorderColor = "gray-200",
  showViewAppLink = true,
  showPreviewImage = true,
  useSimpleImage = false,
}: AppListSectionProps) {
  const [selectedApp, setSelectedApp] = useState(0);

  const appList = apps;

  useEffect(() => {
    if (appList.length === 0) return;

    const interval = setInterval(() => {
      setSelectedApp(prev => (prev + 1) % appList.length);
    }, autoRotateInterval);

    return () => clearInterval(interval);
  }, [appList.length, autoRotateInterval]);

  if (appList.length === 0) {
    return null;
  }

  return (
    <div className="container mx-auto px-8 xl:px-0">
      <div className={`grid ${showPreviewImage ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} gap-12 items-center`}>
        <div className="space-y-1">
          {appList.map((app, index) => (
            <div key={index}>
              <div className="block">
                <div
                  className={`flex items-center p-4 mb-1 rounded-lg hover:shadow-md transition-all duration-200 group cursor-pointer ${
                    selectedApp === index
                      ? `shadow-md border ${activeBorderColor === "red" ? "border-red-500" : "border-gray-200"}`
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => setSelectedApp(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar' || e.code === 'Space') {
                      e.preventDefault();
                      setSelectedApp(index);
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  aria-label={`Select ${app.title} app`}
                  aria-pressed={selectedApp === index}
                >
                  <div className="mr-4 rounded-sm ">
                    <Image
                      src={imgBaseUrl ? imgBaseUrl  + app.icon : app.icon}
                      alt={app.imageAlt}
                      title={app.imageAlt}
                      width={80}
                      height={80}
                      className="inline w-full h-full"
                      style={{ width: "80px", height: "80px" }}
                    />
                  </div>
                  <div className="w-full">
                    <div className="flex flex-col w-full">
                      <Typography variant="h3" as="h3" className="text-lg">{app.title}</Typography>
                      <Typography variant="small" as="p">{app.description}</Typography>
                    </div>
                    {showViewAppLink && (
                      <Link
                        href={app.href}
                        aria-label={`Learn more about ${app.title}`}
                        className="text-xs cursor-pointer opacity-30 group-hover:opacity-100 transition-opacity duration-200 group-hover:text-accent"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent triggering the parent click
                          setSelectedApp(index);
                        }}
                      >
                        View This App
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
          <a href="/digital-signage-apps" className="inline-flex gap-2 mt-6 rounded-md border border-text-secondary px-4 py-2 hover:text-accent hover:border-accent">
            Explore 100+ Digital Signage Apps
            <svg viewBox="0 0 20 20" className="size-6 stroke-[1.5px] fill-none group-hover:stroke-accent stroke-black transform-gpu transition-all duration-500 ease-out">
              <line x1="0" y1="10" x2="29" y2="10" className="origin-left -translate-x-[12px] scale-x-75 group-hover:scale-x-100 transform-gpu transition-transform duration-500 ease-out"/>
              <polyline points="9 5 14 10 9 15" className="-translate-x-[3px] group-hover:translate-x-[3px] transform-gpu transition-transform duration-500 ease-out"/>
            </svg>
          </a>
        </div>

        {showPreviewImage && appList[selectedApp]?.imageUrl && (
          <>
            {useSimpleImage ? (
              // Simple image without absolute positioning
              <div className="w-full h-full">
                <Image
                  src={imgBaseUrl ? imgBaseUrl + appList[selectedApp].imageUrl : appList[selectedApp].imageUrl}
                  alt={appList[selectedApp].title}
                  title={appList[selectedApp].title}
                  width={1200}
                  height={700}
                  className="w-full h-auto rounded-lg object-cover"
                />
              </div>
            ) : (
              // Complex structure with absolute positioning (default)
              <div className="relative border-3 border-black rounded-sm w-full ml-2 mr-2">
                <div className="aspect-video bg-gray-200 relative z-[1] overflow-hidden">
                  <Image
                    src={imgBaseUrl ? imgBaseUrl + appList[selectedApp].imageUrl : appList[selectedApp].imageUrl}
                    alt={appList[selectedApp].title}
                    title={appList[selectedApp].title}
                    fill
                    className="object-cover transition-opacity duration-500"
                  />
                </div>

                <div className="absolute left-[13%] top-[15%] lg:left-[17%] lg:top-[18%] z-0 w-[90%] h-full">
                  <Image
                    src={
                      env.NEXT_PUBLIC_CDN_URL +
                      "/images/digital-signage-apps/flight-app/screen-befrore.FK5t4M77SzVC.svg"
                    }
                    alt="screen before"
                    title="screen before"
                    width={780}
                    height={440}
                    className="w-full h-full object-contain rounded-sm"
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
